import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { SearchAndFilterBar } from '../../components/search-and-filter-bar';
import { getLoadHistoryFilterConfig } from '../../components/modals/universal-filter/filterConfigs';
import loadService from '../../services/loadService';
import userService from '../../services/userService';
import Loading from '../../components/loading-animation';
import LoadHistoryTimeline from './components/LoadHistoryTimeline';
import EmptyState from '../../components/not-result';
import './LoadHistoryPage.scss';

const LoadHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();

  const [loadData, setLoadData] = useState(null);
  const [users, setUsers] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 50
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    action: '',
    userId: '',
    dateFrom: '',
    dateTo: ''
  });
  const filterConfig = useMemo(() => getLoadHistoryFilterConfig(users), [users]);

  useEffect(() => {
    const fetchLoadData = async () => {
      try {
        const response = await loadService.getLoadById(id);
        const load = response?.data?.data || response?.data || response;
        setLoadData(load);
      } catch (err) {
        console.error('Error fetching load data:', err);
      }
    };

    if (id) {
      fetchLoadData();
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const res = await userService.getUsers({ limit: 500 });
        const data = res?.data?.data ?? res?.data ?? res;
        const arr = Array.isArray(data) ? data : data?.users ?? data?.list ?? [];
        if (!cancelled) setUsers(Array.isArray(arr) ? arr : []);
      } catch (e) {
        if (!cancelled) setUsers([]);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!id) return;

    setHistoryLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(filters.action && { action: filters.action }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
      };

      const response = await loadService.getLoadHistory(id, params);
      const apiResponse = response?.data || response;

      if (apiResponse?.success && apiResponse?.data) {
        setHistoryData(apiResponse.data);
        if (apiResponse.pagination) {
          setPagination(prev => ({
            ...prev,
            ...apiResponse.pagination
          }));
        }
      } else {
        throw new Error('Failed to load history data');
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
      setLoading(false);
    }
  }, [id, pagination.currentPage, pagination.limit, filters]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  if (loading && !loadData) {
    return (
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        <Loading size="large" />
      </Container>
    );
  }

  return (
    <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <div className="load-history-page">
        <div className="load-history-page__header">
          <button
            type="button"
            className="load-history-page__back"
            onClick={() => navigate('/loads')}
          >
            ← Back to Loads
          </button>
          <div className="load-history-page__title">
            <h1>Load History</h1>
            {loadData && (
              <p className="load-history-page__subtitle">
                Order ID: <strong>{loadData.orderId || loadData.billOfLadingNumber || id}</strong>
              </p>
            )}
          </div>
        </div>

        <SearchAndFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterApply={handleFilterApply}
          filterConfig={filterConfig}
          searchPlaceholder="Search by field, user..."
          pageType="loads"
          disableAddModal
          showAddButton={false}
          initialFilters={filters}
        />

        {/* History Timeline */}
        {historyLoading ? (
          <Loading size="medium" />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load history"
            message={error}
            onRetry={fetchHistory}
            retryText="Retry"
          />
        ) : historyData.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No history found"
            message="This load has no change history yet."
          />
        ) : (
          <LoadHistoryTimeline
            events={historyData}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </Container>
  );
};

export default LoadHistoryPage;
